import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './InscriptionPage.module.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Credenciales publicas de OpenPay (merchant ID y llave publica)
const OP_MERCHANT_ID = process.env.REACT_APP_OPENPAY_COMMERCE_ID || '';
const OP_PUBLIC_KEY = process.env.REACT_APP_OPENPAY_PUBLIC_KEY || '';
const OP_SANDBOX = (
  String(process.env.REACT_APP_OPENPAY_SANDBOX || '').trim().toLowerCase() === 'true'
);

// Singleton: evita doble inicialización en React Strict Mode (doble-montaje en dev)
let _openpayPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      // La tag ya existe — escuchar load si aún no terminó, o resolver de inmediato
      if (existing.dataset.loaded) { resolve(); return; }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => { s.dataset.loaded = '1'; resolve(); };
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function initOpenPay() {
  if (_openpayPromise) return _openpayPromise;

  _openpayPromise = (async () => {
    await loadScript('https://js.openpay.mx/openpay.v1.min.js');

    const OP = window.OpenPay;
    if (!OP) throw new Error('openpay.v1.min.js cargó pero window.OpenPay no está definido.');
    if (!OP_MERCHANT_ID || !OP_PUBLIC_KEY) {
      throw new Error('Faltan credenciales publicas de OpenPay.');
    }

    OP.setId(OP_MERCHANT_ID);
    OP.setApiKey(OP_PUBLIC_KEY);
    OP.setSandboxMode(OP_SANDBOX);

    await loadScript('https://js.openpay.mx/openpay-data.v1.min.js');

    let deviceSessionId = '';
    try {
      deviceSessionId = OP.deviceData.setup('openpay-form', 'deviceSessionId') || '';
    } catch (err) {
      console.error('OpenPay device data setup error:', err);
      throw err;
    }
    return deviceSessionId;
  })();

  return _openpayPromise;
}

const formatMXN = (amount) =>
  Number(amount).toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  });

const getCleanCardNumber = (cardNumber) => cardNumber.replace(/\D/g, '');

const getDeviceSessionId = (deviceSessionIdRef) => (
  deviceSessionIdRef.current
  || document.getElementById('deviceSessionId')?.value
  || ''
);

const getPaymentErrorMessage = (errorCode) => (
  String(errorCode) === '3003' ? 'Fondos insuficientes.' : 'Tarjeta declinada.'
);

function InscriptionStep4Page() {
  const [searchParams] = useSearchParams();

  const id = searchParams.get('id') || '';
  const careerLabel = searchParams.get('careerLabel') || '';
  const planLabel = searchParams.get('planLabel') || '';
  const paymentMode = searchParams.get('paymentMode') || '';
  const isFlexiblePayment = paymentMode === 'flexible';
  const planTotal = searchParams.get('planTotal')
    ? Number(searchParams.get('planTotal'))
    : null;

  const [card, setCard] = useState({
    holderName: '',
    cardNumber: '',
    expirationMonth: '',
    expirationYear: '',
    cvv2: '',
  });
  const [status, setStatus] = useState('form'); // 'form' | 'processing' | 'success' | 'error'
  const [error, setError] = useState('');
  const [chargeResult, setChargeResult] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [inscriptionRequirementsAccepted, setInscriptionRequirementsAccepted] = useState(false);

  const deviceSessionIdRef = useRef('');
  const openpayReady = useRef(false);
  const [isOpenpayReady, setIsOpenpayReady] = useState(false);

  useEffect(() => {
    initOpenPay()
      .then((deviceSessionId) => {
        deviceSessionIdRef.current = deviceSessionId || getDeviceSessionId(deviceSessionIdRef);
        openpayReady.current = true;
        setIsOpenpayReady(true);
      })
      .catch((err) => {
        console.error('OpenPay init error:', err);
        setError('No se pudo inicializar OpenPay. Recarga la página e intenta de nuevo.');
      });
  }, []);

  const handleCardChange = (e) => {
    const { name, value } = e.target;
    setCard((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!id) {
      setError('Enlace de inscripción inválido.');
      return;
    }

    if (!termsAccepted) {
      setError('Debes aceptar los términos y condiciones.');
      return;
    }

    if (!inscriptionRequirementsAccepted) {
      setError('Debes leer y aceptar los requisitos de inscripción.');
      return;
    }

    const OP = window.OpenPay;
    if (!OP) {
      setError('No se pudo cargar el módulo de pago. Recarga la página.');
      return;
    }

    if (!openpayReady.current) {
      setError('OpenPay todavía se está inicializando. Intenta de nuevo en unos segundos.');
      return;
    }

    if (
      !card.holderName.trim() ||
      !card.cardNumber.trim() ||
      !card.expirationMonth.trim() ||
      !card.expirationYear.trim() ||
      !card.cvv2.trim()
    ) {
      setError('Por favor completa todos los datos de la tarjeta.');
      return;
    }

    const cleanCardNumber = getCleanCardNumber(card.cardNumber);
    if (!OP.card.validateCardNumber(cleanCardNumber)) {
      setError(getPaymentErrorMessage());
      return;
    }

    if (!OP.card.validateExpiry(card.expirationMonth, card.expirationYear)) {
      setError(getPaymentErrorMessage());
      return;
    }

    if (!OP.card.validateCVC(card.cvv2, cleanCardNumber)) {
      setError(getPaymentErrorMessage());
      return;
    }

    const deviceSessionId = getDeviceSessionId(deviceSessionIdRef);
    if (!deviceSessionId) {
      setError('No se pudo generar la sesión de seguridad de Openpay. Recarga la página.');
      return;
    }

    setStatus('processing');

    OP.token.create(
      {
        holder_name: card.holderName,
        card_number: cleanCardNumber,
        expiration_month: card.expirationMonth,
        expiration_year: card.expirationYear,
        cvv2: card.cvv2,
      },
      async (response) => {
        const tokenId = response.data.id;

        try {
          const res = await fetch(`${API_URL}/payment/charge`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userDataId: id,
              tokenId,
              deviceSessionId,
            }),
          });

          const data = await res.json();

          if (!res.ok) {
            const isPaymentError = res.status === 402 || Boolean(data.errorCode);
            setError(
              isPaymentError
                ? getPaymentErrorMessage(data.errorCode)
                : data.message || 'No se pudo completar el pago. Intenta de nuevo.'
            );
            setStatus('form');
            return;
          }

          setChargeResult(data);
          setStatus('success');
        } catch {
          setError('No se pudo conectar con el servidor. Intenta de nuevo.');
          setStatus('form');
        }
      },
      (response) => {
        const code = response.data?.error_code;
        setError(getPaymentErrorMessage(code));
        setStatus('form');
      }
    );
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 12 }, (_, i) => String(currentYear + i).slice(-2));

  // ── Render ────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <main className={`${styles.main} ${styles.mainNoHeader}`}>
        <img
          className={styles.odmLogo}
          src="/logos/logos_2.png"
          alt="Logo ODM"
          onError={(e) => {
            e.target.src = '/logos/logos_1.png';
          }}
        />

        <div className={`${styles.titleBlock} ${styles.titleBlockNoHeader}`}>
          <h1 className={styles.pageTitle}>Inscripciones</h1>
        </div>

        {/* ── Plan summary ── */}
        {planLabel && planTotal != null && (
          <div className={styles.planSummaryBar}>
            {careerLabel && (
              <span>
                Carrera: <strong>{careerLabel}</strong>
              </span>
            )}
            <span>
              Plan: <strong>{planLabel}</strong>
            </span>
            <span>
              Total: <strong>{formatMXN(planTotal)}</strong>
            </span>
          </div>
        )}

        {status === 'success' ? (
          /* ── Success state ── */
          <div className={styles.formCard}>
            <h2 className={styles.step2Title}>¡Pago realizado con éxito!</h2>
            <p className={styles.step2Text}>
              Tu inscripción ha sido procesada correctamente.
              <br />
              Hemos enviado a tu correo electrónico los datos de acceso a tu
              portal académico.
            </p>
            {chargeResult && (
              <p className={styles.step2Text}>
                Tarjeta:{' '}
                <strong>
                  {chargeResult.brand?.toUpperCase()} •••• {chargeResult.last4}
                </strong>
                {' '}— Pago único procesado correctamente
              </p>
            )}
            <p className={styles.step2Warning}>
              ⚠️ Si no recibes el correo en los próximos minutos, revisa tu
              carpeta de spam.
            </p>
          </div>
        ) : (
          /* ── Payment form ── */
          <form
            id="openpay-form"
            className={styles.formCard}
            onSubmit={handleSubmit}
            noValidate
          >
            {/* Hidden device session field (populated by OpenPay.js) */}
            <input type="hidden" id="deviceSessionId" name="deviceSessionId" />

            <h2 className={styles.step2Title}>Datos de pago</h2>

            {/* Card logos */}
            <div className={styles.cardLogosRow}>
              <img src="/openpay/visa.png" alt="Visa" className={styles.cardLogo} />
              <img
                src="/openpay/masterCard.png"
                alt="Mastercard"
                className={styles.cardLogo}
              />
              <img
                src="/openpay/americanExpress.png"
                alt="American Express"
                className={styles.cardLogo}
              />
              <img src="/openpay/carnet.png" alt="Carnet" className={styles.cardLogo} />
            </div>

            {/* Card number */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="cardNumber">
                Número de tarjeta
              </label>
              <input
                id="cardNumber"
                name="cardNumber"
                type="text"
                inputMode="numeric"
                className={styles.field}
                placeholder="1234 5678 9012 3456"
                maxLength={19}
                value={card.cardNumber}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '').slice(0, 16);
                  const formatted = raw.replace(/(.{4})/g, '$1 ').trim();
                  setCard((prev) => ({ ...prev, cardNumber: formatted }));
                }}
              />
            </div>

            {/* Cardholder name */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="holderName">
                Nombre del titular
              </label>
              <input
                id="holderName"
                name="holderName"
                type="text"
                className={styles.field}
                placeholder="Como aparece en la tarjeta"
                value={card.holderName}
                onChange={handleCardChange}
                autoComplete="cc-name"
              />
            </div>

            {/* Expiry + CVV row */}
            <div className={styles.fieldRow}>
              <div className={`${styles.fieldGroup} ${styles.fieldGroupHalf}`}>
                <label className={styles.fieldLabel}>Vencimiento</label>
                <div className={styles.expiryRow}>
                  <select
                    name="expirationMonth"
                    className={`${styles.field} ${styles.fieldSelect}`}
                    value={card.expirationMonth}
                    onChange={handleCardChange}
                  >
                    <option value="">MM</option>
                    {Array.from({ length: 12 }, (_, i) => {
                      const m = String(i + 1).padStart(2, '0');
                      return (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      );
                    })}
                  </select>
                  <select
                    name="expirationYear"
                    className={`${styles.field} ${styles.fieldSelect}`}
                    value={card.expirationYear}
                    onChange={handleCardChange}
                  >
                    <option value="">AA</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={`${styles.fieldGroup} ${styles.fieldGroupHalf}`}>
                <label className={styles.fieldLabel} htmlFor="cvv2">
                  CVV
                </label>
                <input
                  id="cvv2"
                  name="cvv2"
                  type="password"
                  inputMode="numeric"
                  className={styles.field}
                  placeholder="•••"
                  maxLength={4}
                  value={card.cvv2}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setCard((prev) => ({ ...prev, cvv2: raw }));
                  }}
                  autoComplete="cc-csc"
                />
              </div>
            </div>

            <div className={styles.paymentBreakdown}>
              <p className={styles.plansTitle}>Desglose de pago</p>
              {careerLabel && (
                <div className={styles.breakdownRow}>
                  <span>Carrera</span>
                  <strong>{careerLabel}</strong>
                </div>
              )}
              <div className={styles.breakdownRow}>
                <span>Beca seleccionada</span>
                <strong>{planLabel || 'Beca seleccionada'}</strong>
              </div>
              <div className={styles.breakdownRow}>
                <span>Pago de inscripción</span>
                <strong>{planTotal != null ? formatMXN(planTotal) : 'Por confirmar'}</strong>
              </div>
              <div className={`${styles.breakdownRow} ${styles.breakdownTotal}`}>
                <span>Total a pagar hoy</span>
                <strong>{planTotal != null ? formatMXN(planTotal) : 'Por confirmar'}</strong>
              </div>
              <p className={styles.paymentDisclaimer}>
                {isFlexiblePayment
                  ? 'Este es un pago único de inscripción. Los pagos posteriores de la beca flexible se realizarán de forma manual con tarjeta de crédito o débito.'
                  : 'Este es un pago único de inscripción. Al continuar, autorizas que esta tarjeta quede domiciliada para los pagos posteriores correspondientes a la beca seleccionada.'}
              </p>
            </div>

            {error && <p className={styles.errorMsg}>{error}</p>}

            {/* OpenPay security badge */}
            <div className={styles.openpayBadge}>
              <img
                src="/openpay/LogotipoOpenpay-01.jpg"
                alt="Openpay"
                className={styles.openpayLogo}
              />
              <span className={styles.openpayBadgeText}>
                Transacciones seguras vía Openpay
              </span>
            </div>

            <label className={styles.termsRow}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={termsAccepted}
                onChange={(event) => setTermsAccepted(event.target.checked)}
              />
              <span className={styles.termsText}>
                Acepto términos y{' '}
                <a
                  href="/Terminos_y_Condiciones_Plataforma_Pagos_Educativos_FINAL.pdf"
                  download
                  className={styles.termsLink}
                  onClick={(event) => event.stopPropagation()}
                >
                  condiciones
                </a>
              </span>
            </label>

            <label className={styles.termsRow}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={inscriptionRequirementsAccepted}
                onChange={(event) => setInscriptionRequirementsAccepted(event.target.checked)}
              />
              <span className={styles.termsText}>
                He leído y acepto los{' '}
                <a
                  href="/inscricpicion.docx.pdf"
                  download
                  className={styles.termsLink}
                  onClick={(event) => event.stopPropagation()}
                >
                  requisitos de inscripción
                </a>
              </span>
            </label>

            <div className={styles.submitRow}>
              <button
                type="submit"
                className={`${styles.submitButton} ${(!termsAccepted || !inscriptionRequirementsAccepted) ? styles.submitButtonDisabledStyle : ''}`}
                disabled={status === 'processing' || !isOpenpayReady || !termsAccepted || !inscriptionRequirementsAccepted}
              >
                {status === 'processing'
                  ? 'Procesando...'
                  : `Pagar ${planTotal != null ? formatMXN(planTotal) : ''}`}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

export default InscriptionStep4Page;
